import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { getDocumentName } from '@e-socna/data-layer';
import { buildPath } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../utils/back-errors.util';
import {
  PATH_COST_GENERAL,
  PATH_COSTS,
} from '../../../app/constants/paths.constant';
import { useViewSnackbarAction } from '../../../hooks/useViewSnackbarAction.hook';
import { CostPage_OneCost_QueryDocument } from '../../../pages/Cost/hooks/useFindOneCostQuery.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { CostListCmp_AllCosts_QueryDocument } from '../../../components/CostList/hooks/useFindAllCostsQuery.hook';
import type { BackError } from '../../../utils/back-errors.util';

import { memoForm2ApiMapper } from '../../MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { FormVariant } from '../../enums/variant.enum';
import type { MemosState } from '../../MemoBeforeSubmit';

import { variableCostForm2ApiMapper } from '../mappers/variable-cost.form2api.mapper';
import { fixedCostForm2ApiMapper } from '../mappers/fixed-cost.form2api.mapper';
import type { CostForm_VariableCost } from '../fragments/variable-cost.fragment';
import type { CostForm_FixedCost } from '../fragments/fixed-cost.fragment';
import type { CostForm_Cost } from '../fragments/cost.fragment';

import { useCreateVariableCostMutation } from './useCreateVariableCostMutation.hook';
import { useCreateFixedCostMutation } from './useCreateFixedCostMutation.hook';
import { useUpdateVariableCostMutation } from './useUpdateVariableCostMutation.hook';
import { useUpdateFixedCostMutation } from './useUpdateFixedCostMutation.hook';
import type { VariableCost_Create_Result } from './useCreateVariableCostMutation.hook';
import type { FixedCost_Create_Result } from './useCreateFixedCostMutation.hook';
import type { VariableCost_Foau_Result } from './useUpdateVariableCostMutation.hook';
import type { FixedCost_Foau_Result } from './useUpdateFixedCostMutation.hook';

export type CostMutationResult =
  | FixedCost_Create_Result
  | VariableCost_Create_Result
  | FixedCost_Foau_Result
  | VariableCost_Foau_Result;

interface Params {
  readonly costId?: Id;
  readonly formVariant?: FormVariant;
  readonly handleSuccess?: (result: CostMutationResult) => void;
  readonly memos: MemosState;
}

export const useCostSubmit = ({
  costId,
  formVariant,
  handleSuccess,
  memos,
}: Readonly<Params>) => {
  const { costTypesCodeMap } = useReferencesContext();

  const [formError, setFormError] = useState<readonly string[] | null>(null);

  const { enqueueSnackbar } = useSnackbar();
  const { buildViewSnackbarAction } = useViewSnackbarAction();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate: createVariableCostMutation } =
    useCreateVariableCostMutation();
  const { mutate: createFixedCostMutation } = useCreateFixedCostMutation();
  const { mutate: updateVariableCostMutation } =
    useUpdateVariableCostMutation();
  const { mutate: updateFixedCostMutation } = useUpdateFixedCostMutation();

  const handleSubmit = useCallback(
    (
      values: CostForm_Cost,
      {
        setSubmitting,
      }: Readonly<{ setSubmitting: (isSubmitting: boolean) => void }>,
    ) => {
      setFormError(null);

      const onSuccess = (result: CostMutationResult) => {
        if (!formVariant || formVariant === FormVariant.Creation) {
          const creationResult = result as
            FixedCost_Create_Result | VariableCost_Create_Result;

          const { _id } =
            'createVariableCost' in creationResult
              ? creationResult.createVariableCost
              : creationResult.createFixedCost;

          enqueueSnackbar('Votre nouveau coût a bien été créé.', {
            action: buildViewSnackbarAction(
              buildPath(PATH_COST_GENERAL, { id: _id }),
            ),
          });
          void queryClient.invalidateQueries({
            queryKey: [getDocumentName(CostListCmp_AllCosts_QueryDocument)],
          });
        } else {
          enqueueSnackbar('Votre coût a bien été mis à jour.');
          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(CostPage_OneCost_QueryDocument),
              { id: costId },
            ],
          });
        }

        if (handleSuccess) {
          handleSuccess(result);
        } else if (!formVariant || formVariant === FormVariant.Creation) {
          void navigate(PATH_COSTS);
        }

        setSubmitting(false);
      };

      const onError = (error: unknown) => {
        setFormError(userFriendlyErrors(error as BackError));

        setSubmitting(false);
      };

      if (!formVariant || formVariant === FormVariant.Creation) {
        if (values.typeId === costTypesCodeMap.VariableCost._id) {
          createVariableCostMutation(
            {
              variableCost: variableCostForm2ApiMapper(
                values as CostForm_VariableCost,
              ),
              versionData: memoForm2ApiMapper(memos),
            },
            {
              onError,
              onSuccess,
            },
          );
        } else {
          createFixedCostMutation(
            {
              fixedCost: fixedCostForm2ApiMapper(values as CostForm_FixedCost),
              versionData: memoForm2ApiMapper(memos),
            },
            {
              onError,
              onSuccess,
            },
          );
        }
      } else if (costId) {
        if (values.typeId === costTypesCodeMap.VariableCost._id) {
          updateVariableCostMutation(
            {
              filter: { _id: costId },
              update: variableCostForm2ApiMapper(
                values as CostForm_VariableCost,
              ),
              versionData: memoForm2ApiMapper(memos),
            },
            {
              onError,
              onSuccess,
            },
          );
        } else {
          updateFixedCostMutation(
            {
              filter: { _id: costId },
              update: fixedCostForm2ApiMapper(values as CostForm_FixedCost),
              versionData: memoForm2ApiMapper(memos),
            },
            {
              onError,
              onSuccess,
            },
          );
        }
      }
    },
    [
      costId,
      buildViewSnackbarAction,
      costTypesCodeMap.VariableCost._id,
      createFixedCostMutation,
      createVariableCostMutation,
      enqueueSnackbar,
      formVariant,
      handleSuccess,
      memos,
      navigate,
      queryClient,
      updateFixedCostMutation,
      updateVariableCostMutation,
    ],
  );

  return { formError, handleSubmit };
};
